import { cn } from "@eisox/design-system";

import {
  getSetpointLines,
  hasDuctSetpoint,
  hasDuctValue,
  type DuctSensors,
  type SetpointLine as Line,
  type SetpointQuantity,
} from "../helpers";
import { Position, type Component } from "../types";
import { Card } from "./card";
import { DefectDot } from "./defect-dot";
import { Indicator } from "./indicator";
import { SetpointLine } from "./setpoint-line";

type SensorsProps = Component &
  DuctSensors & {
    buildingId: string;
    airHandlerUnitId: string;
  };

const TILE = "min-w-33 box-border";

const Sensors: React.FC<SensorsProps> = ({
  buildingId,
  airHandlerUnitId,
  flow,
  flowDefect,
  flowSetpoint,
  flowActualSetpoint,
  pressure,
  pressureSetpoint,
  pressureActualSetpoint,
  pressureDefect,
  co2,
  co2Defect,
  co2Setpoint,
  co2ActualSetpoint,
  humidity,
  humidityDefect,
  humiditySetpoint,
  humidityActualSetpoint,
  position = Position.TOP,
}) => {
  const quantities: { key: SetpointQuantity; lines: Line[]; format: (value: number) => string }[] = [
    {
      key: "flow",
      lines: getSetpointLines(flowActualSetpoint, flowSetpoint),
      format: value => `D : ${value.toFixed(0)} m³/h`,
    },
    {
      key: "pressure",
      lines: getSetpointLines(pressureActualSetpoint, pressureSetpoint),
      format: value => `P : ${value.toFixed(0)} Pa`,
    },
    {
      key: "humidity",
      lines: getSetpointLines(humidityActualSetpoint, humiditySetpoint),
      format: value => `H : ${value.toFixed(1)} %`,
    },
    {
      key: "co2",
      lines: getSetpointLines(co2ActualSetpoint, co2Setpoint),
      format: value => `CO₂ : ${value.toFixed(1)} ppm`,
    },
  ];

  return (
    <div
      className={cn("gap-1.25 flex w-max flex-col items-center", position === Position.BOTTOM && "flex-col-reverse")}
    >
      {hasDuctSetpoint({
        flowSetpoint,
        flowActualSetpoint,
        pressureSetpoint,
        pressureActualSetpoint,
        humiditySetpoint,
        humidityActualSetpoint,
        co2Setpoint,
        co2ActualSetpoint,
      }) && (
        <Card className={cn(TILE, "gap-0 flex flex-col items-center")}>
          {quantities.flatMap(({ key, lines, format }) =>
            lines.map(line => (
              <SetpointLine
                key={`${key}-${line.kind}`}
                kind={line.kind}
                section={key}
                muted={line.muted}
                buildingId={buildingId}
                airHandlerUnitId={airHandlerUnitId}
              >
                {format(line.value)}
              </SetpointLine>
            )),
          )}
        </Card>
      )}
      {hasDuctValue({ flow, pressure, co2, humidity }) && (
        <Indicator position={position} className={TILE}>
          {flow !== undefined && (
            <span className="gap-1 flex items-center">
              {`D : ${flow.toFixed(0)} m³/h`}
              <DefectDot defect={flowDefect} />
            </span>
          )}
          {pressure !== undefined && (
            <span className="gap-1 flex items-center">
              {`P : ${pressure.toFixed(0)} Pa`}
              <DefectDot defect={pressureDefect} />
            </span>
          )}
          {humidity !== undefined && (
            <span className="gap-1 flex items-center">
              {`H : ${humidity.toFixed(1)} %`}
              <DefectDot defect={humidityDefect} />
            </span>
          )}
          {co2 !== undefined && (
            <span className="gap-1 flex items-center">
              {`CO₂ : ${co2.toFixed(1)} ppm`}
              <DefectDot defect={co2Defect} />
            </span>
          )}
        </Indicator>
      )}
    </div>
  );
};

Sensors.displayName = "Sensors";

export { Sensors };
export type { SensorsProps };
